import { useContext, useMemo } from "react"
import { FAAttributionDataContext } from "."

export function useAttributionContext() {
  const ctx = useContext(FAAttributionDataContext);
  return ctx;
}

export function useAttributionData() {
  const ctx = useAttributionContext();
  const data = ctx.dataStore;
  return data;
}

export function useAttributionDateRange() {
  const ctx = useAttributionContext();
  const { beginDate, endDate } = ctx;
  const dateRange = useMemo(() => {
    if (beginDate == null || endDate == null) return null;
    return { beginDate, endDate };
  }, [beginDate, endDate]);
  return dateRange;
}

export function useAttributionTotalReturns() {
  const data = useAttributionData();
  return data?.totalReturn ?? null;
}

export function useAttributionDurations() {
  const data = useAttributionData();
  return data?.attrDurations ?? null;
}

export function useAttributionDailySummaries() {
  const data = useAttributionData();
  return data?.dailySummaries ?? null;
}

export function useAttributionPeriodLabel() {
  const range = useAttributionDateRange();
  return useMemo(() => {
    if (!range) return undefined;
    return `${range.beginDate.format('M/D/YYYY')} to ${range.endDate.format('M/D/YYYY')}`;
  }, [range]);
}

export function useAttributionFactorContributionFreq() {
  const ctx = useAttributionContext();
  const data = ctx.dataStore;
  // When a zoom range is active, the card reflects the zoomed (finer) data.
  if (ctx.zoomPeriod) return data?.factorContributionFreqZoom ?? null;
  return data?.factorContributionFreq ?? null;
}

// Per-factor totals over the active range (no frequency) — drives the strip and table.
export function useAttributionFactorContributionTotal() {
  const ctx = useAttributionContext();
  const data = ctx.dataStore;
  if (ctx.zoomPeriod) return data?.factorContributionTotalZoom ?? null;
  return data?.factorContributionTotal ?? null;
}

export function useAttributionSecurityContribution() {
  const data = useAttributionData();
  return data?.securityContribution ?? null;
}

export function useAttributionZoom() {
  const ctx = useAttributionContext();
  return {
    zoomPeriod: ctx.zoomPeriod,
    setZoomPeriod: ctx.setZoomPeriod,
    zoomLoading: ctx.zoomLoading,
    zoomError: ctx.zoomError,
  };
}

export function useAttributionLoading() {
  const ctx = useAttributionContext();
  return ctx.loading;
}

export function useAttributionReportId() {
  const ctx = useAttributionContext();
  return ctx.reportId;
}

export function useAttributionPortfolioName() {
  const ctx = useAttributionContext();
  return ctx.portfolioName;
}

export function useAttributionFactorFreqError() {
  const ctx = useAttributionContext();
  return ctx.factorFreqError;
}

export function useAttributionSecurityError() {
  const ctx = useAttributionContext();
  return ctx.securityError;
}